import { useState, useEffect } from "react";
import { createAdminApiClient, publicApi } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const AdminComparisons = () => {
    const [comparisons, setComparisons] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingTable, setEditingTable] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    
    const emptyTable = {
        name: "",
        description: "",
        features: [],
        products: [],
        is_active: true
    };
    
    const [formData, setFormData] = useState(emptyTable);
    const [tempFeature, setTempFeature] = useState({ name: "", key: "" });
    const [selectedProductId, setSelectedProductId] = useState("");
    
    const getAdminApi = () => {
        const credStr = sessionStorage.getItem('adminCredentials');
        if (!credStr) return null;
        const credentials = JSON.parse(credStr);
        return createAdminApiClient(credentials.username, credentials.password);
    };
    
    const fetchData = async () => {
        try {
            const adminApi = getAdminApi();
            if (!adminApi) {
                setLoading(false);
                return;
            }
            const [compRes, prodRes] = await Promise.all([
                adminApi.getComparisons(),
                publicApi.getProducts()
            ]);
            setComparisons(compRes.data);
            setProducts(prodRes.data);
        } catch (err) {
            console.error("Failed to fetch data:", err);
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const generateKey = (name) => {
        return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
    };
    
    const handleEdit = (table) => {
        setEditingTable(table);
        setFormData(table);
        setIsDialogOpen(true);
    };
    
    const handleCreate = () => {
        setEditingTable(null);
        setFormData(emptyTable);
        setIsDialogOpen(true);
    };
    
    const handleSave = async () => {
        if (!formData.name) {
            toast.error("Name is required");
            return;
        }
        
        try {
            const adminApi = getAdminApi();
            if (editingTable) {
                await adminApi.updateComparison(editingTable.id, formData);
                toast.success("Comparison table updated");
            } else {
                await adminApi.createComparison(formData);
                toast.success("Comparison table created");
            }
            setIsDialogOpen(false);
            fetchData();
        } catch (err) {
            console.error("Failed to save comparison:", err);
            toast.error("Failed to save comparison table");
        }
    };
    
    const handleDelete = async (tableId) => {
        if (!window.confirm("Are you sure you want to delete this comparison table?")) return;
        
        try {
            const adminApi = getAdminApi();
            await adminApi.deleteComparison(tableId);
            toast.success("Comparison table deleted");
            fetchData();
        } catch (err) {
            console.error("Failed to delete comparison:", err);
            toast.error("Failed to delete comparison table");
        }
    };
    
    const addFeature = () => {
        if (!tempFeature.name) return;
        const key = tempFeature.key || generateKey(tempFeature.name);
        setFormData(prev => ({
            ...prev,
            features: [...prev.features, { name: tempFeature.name, key }]
        }));
        setTempFeature({ name: "", key: "" });
    };
    
    const removeFeature = (index) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };
    
    const addProduct = () => {
        if (!selectedProductId) return;
        const product = products.find(p => p.id === selectedProductId);
        if (!product) return;
        
        // Check if already added
        if (formData.products.some(p => p.product_id === selectedProductId)) {
            toast.error("Product already added to comparison");
            return;
        }
        
        // Initialize values for all features
        const values = {};
        formData.features.forEach(f => { values[f.key] = ""; });
        
        setFormData(prev => ({
            ...prev,
            products: [...prev.products, {
                product_id: product.id,
                product_name: product.display_title,
                values
            }]
        }));
        setSelectedProductId("");
    };
    
    const removeProduct = (index) => {
        setFormData(prev => ({
            ...prev,
            products: prev.products.filter((_, i) => i !== index)
        }));
    };
    
    const updateProductValue = (productIndex, featureKey, value) => {
        setFormData(prev => ({
            ...prev,
            products: prev.products.map((p, i) => 
                i === productIndex 
                    ? { ...p, values: { ...p.values, [featureKey]: value } }
                    : p
            )
        }));
    };
    
    return (
        <div data-testid="admin-comparisons">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Comparison Tables</h1>
                    <p className="text-muted-foreground">Create and manage product comparison tables</p>
                </div>
                <Button 
                    onClick={handleCreate}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider"
                    data-testid="create-comparison-btn"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Table
                </Button>
            </div>
            
            {/* Tables List */}
            <div className="grid gap-6">
                {loading ? (
                    <Card className="bg-card border border-border rounded-none">
                        <CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent>
                    </Card>
                ) : comparisons.length === 0 ? (
                    <Card className="bg-card border border-border rounded-none">
                        <CardContent className="p-8 text-center text-muted-foreground">
                            No comparison tables yet. Click "Add Table" to create your first table.
                        </CardContent>
                    </Card>
                ) : (
                    comparisons.map((table) => (
                        <Card key={table.id} className="bg-card border border-border rounded-none">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="font-heading font-bold text-lg">{table.name}</h3>
                                        <p className="text-muted-foreground text-sm">{table.description || "No description"}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => handleEdit(table)} className="rounded-none">
                                            <Pencil className="h-4 w-4 mr-1" />
                                            Edit
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleDelete(table.id)} className="text-destructive hover:text-destructive rounded-none">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                
                                <div className="text-sm text-muted-foreground">
                                    <span>{table.features?.length || 0} features</span>
                                    <span className="mx-2">•</span>
                                    <span>{table.products?.length || 0} products</span>
                                    <span className="mx-2">•</span>
                                    <span className={table.is_active ? "text-green-500" : "text-red-500"}>
                                        {table.is_active ? "Active" : "Inactive"}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
            
            {/* Edit/Create Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-card border-border rounded-none max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl">
                            {editingTable ? "Edit Comparison Table" : "Create Comparison Table"}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Table Name *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="Equipment Comparison"
                                    data-testid="comparison-name"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={formData.is_active}
                                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                />
                                <Label>Active (Visible on Site)</Label>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                rows={2}
                            />
                        </div>
                        
                        {/* Features */}
                        <div className="space-y-2 pt-4 border-t border-border">
                            <Label className="font-mono text-xs uppercase tracking-wider">Comparison Features</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={tempFeature.name}
                                    onChange={(e) => setTempFeature({ ...tempFeature, name: e.target.value, key: generateKey(e.target.value) })}
                                    className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                                    placeholder="Feature Name (e.g., Air Consumption)"
                                />
                                <Input
                                    value={tempFeature.key}
                                    onChange={(e) => setTempFeature({ ...tempFeature, key: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary w-40"
                                    placeholder="Key"
                                />
                                <Button type="button" onClick={addFeature} className="rounded-none">Add</Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {formData.features.map((feature, index) => (
                                    <div key={index} className="bg-secondary/50 px-3 py-1 flex items-center gap-2">
                                        <span>{feature.name}</span>
                                        <span className="text-muted-foreground text-xs">({feature.key})</span>
                                        <button type="button" onClick={() => removeFeature(index)} className="text-destructive">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Products */}
                        <div className="space-y-2 pt-4 border-t border-border">
                            <Label className="font-mono text-xs uppercase tracking-wider">Products to Compare</Label>
                            <div className="flex gap-2">
                                <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                                    <SelectTrigger className="rounded-none bg-input border-transparent flex-1">
                                        <SelectValue placeholder="Select a product..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-card border-border rounded-none">
                                        {products.map((product) => (
                                            <SelectItem key={product.id} value={product.id} className="rounded-none">
                                                {product.display_title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button type="button" onClick={addProduct} className="rounded-none">Add</Button>
                            </div>
                        </div>
                        
                        {/* Comparison Matrix */}
                        {formData.features.length > 0 && formData.products.length > 0 && (
                            <div className="space-y-2 pt-4 border-t border-border overflow-x-auto">
                                <Label className="font-mono text-xs uppercase tracking-wider">Comparison Values</Label>
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-border bg-secondary/50">
                                            <TableHead className="font-mono uppercase text-xs">Feature</TableHead>
                                            {formData.products.map((product, index) => (
                                                <TableHead key={index} className="font-mono uppercase text-xs">
                                                    <div className="flex items-center gap-2">
                                                        {product.product_name}
                                                        <button type="button" onClick={() => removeProduct(index)} className="text-destructive">
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {formData.features.map((feature) => (
                                            <TableRow key={feature.key} className="border-border">
                                                <TableCell className="font-medium">{feature.name}</TableCell>
                                                {formData.products.map((product, productIndex) => (
                                                    <TableCell key={productIndex}>
                                                        <Input
                                                            value={product.values[feature.key] || ""}
                                                            onChange={(e) => updateProductValue(productIndex, feature.key, e.target.value)}
                                                            className="rounded-none bg-input border-transparent focus:border-primary h-8 text-sm"
                                                            placeholder="—"
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" className="rounded-none">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleSave} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none" data-testid="save-comparison-btn">
                            {editingTable ? "Save Changes" : "Create Table"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminComparisons;
